import {
  type TableColumnFormatNtpSummarySources,
  getValueFromKeyOfType,
} from '@ipfabric/utils';
import type { RenderedTableCell } from 'components/Table/cells/RenderedTableCell';
import {
  ARRAY_CELL_MAX_ITEMS,
  maybeWrapWithMore,
} from 'components/Table/cells/helpers/trimLargeArray';
import { array, option } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import { Fragment, type ReactNode } from 'react';
import type { RenderCellContentArguments } from '../model/TableCellContentRenderFunction';

const getSourcesColor = (sync?: 'yes' | 'no' | 'unknown') => {
  if (sync === 'yes') {
    return 'text-green-500';
  }
  if (sync === 'no') {
    return 'text-red-500';
  }
  return 'text-gray-500';
};

const getFormattedContent = <TRow,>(
  originalContent: unknown,
  format: TableColumnFormatNtpSummarySources<TRow, unknown>,
): ReactNode => {
  return Array.isArray(originalContent) ? (
    pipe(
      originalContent,
      (a) => a.slice(0, ARRAY_CELL_MAX_ITEMS),
      array.mapWithIndex((index, value) => (
        <Fragment key={index}>
          <span className={getSourcesColor(value.sync)}>
            {format.toWebString(value)}
          </span>
          <span className='text-gray-400'>
            {index !== originalContent.length - 1 && ', '}
          </span>
        </Fragment>
      )),
      maybeWrapWithMore(originalContent),
    )
  ) : (
    <span className={getSourcesColor()}>
      {format.toWebString(originalContent)}
    </span>
  );
};

export const renderNtpSummarySourcesCell = <TRow,>(
  format: TableColumnFormatNtpSummarySources<TRow, unknown>,
  { row }: RenderCellContentArguments<TRow>,
): RenderedTableCell => {
  const originalContent = getValueFromKeyOfType(row, format.field);

  return pipe(
    originalContent,
    option.fromNullable,
    option.fold(
      () => ({
        content: null,
      }),
      (content: unknown) => ({
        content: getFormattedContent(content, format),
      }),
    ),
  );
};
